import _pick from 'lodash/pick';
import _memoize from 'lodash/memoize';
import fetch from '@zillow/fetchier';

// @ts-check

/**
 * Determine whether a port number should be added
 * @param {String} protocol
 * @param {Number} port
 * @returns {Boolean}
 */
var shouldAddPort = function shouldAddPort(protocol, port) {
  return Boolean(port) && !(protocol === 'http' && port === 80 || protocol === 'https' && port === 443);
};
/**
 * Build endpoint from params
 * @param {String} protocol
 * @param {String} host
 * @param {Number} port
 * @returns {String} protocol://host:port
 */

var buildEndpoint = function buildEndpoint(protocol, host, port) {
  return protocol + "://" + host + (shouldAddPort(protocol, Number(port)) ? ":" + port : '');
};

/** @typedef {string} TrialName The name of an AB trial, usually formatted as ALL_CAPS_SNAKE_CASE */

/** @typedef {string} Treatment The assigned value of an AB trial, usually "CONTROL" or "ON" */

/** @typedef {{ [trialName: string]: { treatment: Treatment }}} ABTrialsDictionary */

/**
 * Filter object values by their keys using the callback provided
 * @param {Object} obj
 * @param {(key: string) => boolean} filterFn should return Boolean/truthy/falsy values
 * @returns {Object}
 */

var filterObjectByKey = function filterObjectByKey(obj, filterFn) {
  return _pick(obj, Object.keys(obj).filter(filterFn));
};
/**
 * Create an instance of AB-related methods with specified configuration.
 * @param {string} guid
 * @param {string} [abUrl] ab service endpoint, defaults to develop
 * @param {number} [timeout] defaults to 1s
 * @param {Object} [agent] an http agent the client can use to pool requests
 * @param {Object} [absHeaders] headers to be passed along with the call to abs
 */

var abClient = function abClient(guid, abUrl, timeout, agent, absHeaders) {
  if (abUrl === void 0) {
    abUrl = 'http://abs.qa.zillow.net';
  }

  if (timeout === void 0) {
    timeout = 1000;
  }

  if (agent === void 0) {
    agent = null;
  }

  if (absHeaders === void 0) {
    absHeaders = {};
  }

  /**
   * Retrieve raw AB trial treatments from configured endpoint.
   * @param {TrialName[]} [trialNames] A list of explicit trial names to pass to AB endpoint
   * @returns {Promise<ABTrialsDictionary>}
   */
  var getTrialsFromEndpoint = function getTrialsFromEndpoint(trialNames) {
    if (trialNames === void 0) {
      trialNames = [];
    }

    return fetch(abUrl + "/v2/trials/" + trialNames.join(','), {
      query: {
        key: guid
      },
      timeout: timeout,
      agent: agent,
      headers: absHeaders
    }).then(function (response) {
      return response.json();
    }).then(function (payload) {
      return payload.trials;
    });
  };
  /**
   * Memoized retrieval of AB trial treatments with optional filtering from configured endpoint.
   * @param {TrialName[]} [trialNames] A list of explicit trial names to pass to AB endpoint
   * @param {string|RegExp} [trialFilter] filter trial names by string/regexp match
   * @returns {Promise<ABTrialsDictionary>}
   */


  var getTrials = function getTrials(trialNames, trialFilter) {
    if (trialNames === void 0) {
      trialNames = [];
    }

    var response = getTrialsFromEndpoint(trialNames);

    if (trialFilter) {
      response = response.then(function (trials) {
        return filterObjectByKey(trials, function (entry) {
          return entry.search(trialFilter) !== -1;
        });
      });
    }

    return response;
  };

  return {
    getTrialsFromEndpoint: getTrialsFromEndpoint,
    getTrials: _memoize(getTrials)
  };
};

export default abClient;
export { buildEndpoint, filterObjectByKey };
